/**
 * Single app theme provider. A wrapper over the registry and MUI ThemeProvider.
 */

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material";
import {
  loadInstalledMarketplaceThemes,
  getThemeById,
  getDefaultTheme,
} from "./registry";
import { setStoredThemeId, setStoredThemeMode } from "./config";
import type { ThemeConfig } from "./types";
import type { ConfigNode } from "@/designer/types/config.types";
import { logger } from "@/utils/logger";
import { themeIdForMode, emitThemeMode, type ThemeMode } from "./mode";
import { withThemeTransition } from "./themeTransition";
import {
  syncConnectionThemes,
  extractThemeNodes,
  defaultThemeIdsFromNodes,
  pickActiveThemeAfterSync,
  getAvailableConfigThemes,
} from "./connectionThemes";

const ThemeContext = React.createContext<{
  currentTheme: ThemeConfig;
  setTheme: (themeId: string) => void;
  availableThemes: ThemeConfig[];
  mode: ThemeMode;
  toggleMode: () => void;
  /** Sync Designer tree themes into the registry and refresh the picker list. */
  applyConnectionThemes: (connectionId: string | null, tree: ConfigNode[]) => void;
} | null>(null);

export function useThemeContext() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeContext must be used within ThemeProvider");
  return ctx;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [currentTheme, setCurrentThemeState] = useState<ThemeConfig>(getDefaultTheme);
  const [ready, setReady] = useState(false);
  const [configEpoch, setConfigEpoch] = useState(0);

  useEffect(() => {
    // Built-in themes are already in the registry from theme/index.ts on import (no dupes in Strict Mode)
    loadInstalledMarketplaceThemes().finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return;
    const theme = getDefaultTheme();
    setCurrentThemeState(theme);
  }, [ready]);

  const setTheme = useCallback((themeId: string) => {
    const theme = getThemeById(themeId);
    if (theme) {
      withThemeTransition(() => setCurrentThemeState(theme));
      setStoredThemeId(themeId);
    } else {
      logger.warn("Theme not found:", themeId);
    }
  }, []);

  const applyConnectionThemes = useCallback(
    (connectionId: string | null, tree: ConfigNode[]) => {
      const nodes = extractThemeNodes(tree);
      const synced = syncConnectionThemes(connectionId, tree);
      const active = pickActiveThemeAfterSync(synced, defaultThemeIdsFromNodes(nodes));
      withThemeTransition(() => setCurrentThemeState(active));
      setConfigEpoch((e) => e + 1);
    },
    [],
  );

  const availableThemes = useMemo(
    () => getAvailableConfigThemes(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, configEpoch, currentTheme?.id],
  );

  const muiTheme = useMemo(
    () => currentTheme.createTheme(),
    [currentTheme]
  );

  const mode: ThemeMode = muiTheme.palette.mode === "dark" ? "dark" : "light";

  useEffect(() => {
    setStoredThemeMode(mode);
  }, [mode]);

  const toggleMode = useCallback(() => {
    const next: ThemeMode = mode === "dark" ? "light" : "dark";
    setTheme(themeIdForMode(next));
    emitThemeMode(next);
  }, [mode, setTheme]);

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        setTheme,
        availableThemes,
        mode,
        toggleMode,
        applyConnectionThemes,
      }}
    >
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
